// Libs
import classNames from 'classnames/bind';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
// Components, Layouts, Pages
import { BasePagination, Loading, UnderDevelopment } from '~/components/common';
// Others
import { ITask } from '~/utils/interface/task';
import { formatDate } from '~/utils/helper/common';
import { StorageEnum } from '~/utils/enum/base';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IQueryParamBase } from '~/utils/interface/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { IPaginationResponse } from '~/utils/interface/base';
import { getStatisticTodayTask } from '~/thunks/statistic/statisticThunk';
import { BusinessTypeEnum, DateFormatEnum, RoleEnum, TypeCrmEnum } from '~/utils/enum/common';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE_DASHBOARD, EMPTY_STRING } from '~/utils/constants/common';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';

// Styles, images, icons
import styles from './TodaysTask.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const TodaysTask = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/dashboard', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listTask, setListTask] = useState<ITask[]>([]);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE_DASHBOARD,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListTaskToday(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListTaskToday = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getStatisticTodayTask(params))
      .unwrap()
      .then((res) => {
        const { responses: listTask, pagination } = res.data;

        if (!listTask) return;

        setListTask(listTask);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSeeAll = () => {
    handleShowUnderDevelopment();
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleDetailTask = (data: ITask) => {
    if (data?.businessType === BusinessTypeEnum.JOB) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(`${adminRouteAbsolute.detailJob(data?.businessId)}#tasks`);
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(`${salesAdminRouteAbsolute.detailJob(data?.businessId)}#tasks`);
          break;
        case RoleEnum.SALES_PERSON:
          navigate(`${salesPersonRouteAbsolute.detailJob(data?.businessId)}#tasks`);
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(`${operationsManagerRouteAbsolute.detailJob(data?.businessId)}#tasks`);
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(`${warehouseLeadRouteAbsolute.detailJob(data?.businessId)}#tasks`);
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(`${warehouseStaffRouteAbsolute.detailJob(data?.businessId)}#tasks`);
          break;
        case RoleEnum.FOREMAN:
          navigate(`${foremanRouteAbsolute.detailJob(data?.businessId)}#tasks`);
          break;
        case RoleEnum.CREW:
          navigate(`${crewRouteAbsolute.detailJob(data?.businessId)}#tasks`);
          break;
        case RoleEnum.LOADER:
          navigate(`${loaderRouteAbsolute.detailJob(data?.businessId)}#tasks`);
          break;
      }
    }

    if (data?.businessType === BusinessTypeEnum.CLIENT) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(`${adminRouteAbsolute.detailClient(data?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(`${salesAdminRouteAbsolute.detailClient(data?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.SALES_PERSON:
          navigate(`${salesPersonRouteAbsolute.detailClient(data?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(`${operationsManagerRouteAbsolute.detailClient(data?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(`${warehouseLeadRouteAbsolute.detailClient(data?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(`${warehouseStaffRouteAbsolute.detailClient(data?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.FOREMAN:
          navigate(`${foremanRouteAbsolute.detailClient(data?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.CREW:
          navigate(`${crewRouteAbsolute.detailClient(data?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
        case RoleEnum.LOADER:
          navigate(`${loaderRouteAbsolute.detailClient(data?.businessId)}?type=${TypeCrmEnum.CLIENT}#tasks`);
          break;
      }
    }

    if (data?.businessType === BusinessTypeEnum.ORDER) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(adminRouteAbsolute.detailOrder(data?.businessId));
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(salesAdminRouteAbsolute.detailOrder(data?.businessId));
          break;
        case RoleEnum.SALES_PERSON:
          navigate(salesPersonRouteAbsolute.detailOrder(data?.businessId));
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(operationsManagerRouteAbsolute.detailOrder(data?.businessId));
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(warehouseLeadRouteAbsolute.detailOrder(data?.businessId));
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(warehouseStaffRouteAbsolute.detailOrder(data?.businessId));
          break;
        case RoleEnum.FOREMAN:
          navigate(foremanRouteAbsolute.detailOrder(data?.businessId));
          break;
        case RoleEnum.CREW:
          navigate(crewRouteAbsolute.detailOrder(data?.businessId));
          break;
        case RoleEnum.LOADER:
          navigate(loaderRouteAbsolute.detailOrder(data?.businessId));
          break;
      }
    }

    if (data?.businessType === BusinessTypeEnum.QUOTE) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(adminRouteAbsolute.detailQuote(data?.businessId));
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(salesAdminRouteAbsolute.detailQuote(data?.businessId));
          break;
        case RoleEnum.SALES_PERSON:
          navigate(salesPersonRouteAbsolute.detailQuote(data?.businessId));
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(operationsManagerRouteAbsolute.detailQuote(data?.businessId));
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(warehouseLeadRouteAbsolute.detailQuote(data?.businessId));
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(warehouseStaffRouteAbsolute.detailQuote(data?.businessId));
          break;
        case RoleEnum.FOREMAN:
          navigate(foremanRouteAbsolute.detailQuote(data?.businessId));
          break;
        case RoleEnum.CREW:
          navigate(crewRouteAbsolute.detailQuote(data?.businessId));
          break;
        case RoleEnum.LOADER:
          navigate(loaderRouteAbsolute.detailQuote(data?.businessId));
          break;
      }
    }

    if (!data?.businessType) {
      switch (currentRole) {
        case RoleEnum.SUPER_ADMIN:
          navigate(adminRouteAbsolute.task);
          break;
        case RoleEnum.SALES_ADMIN:
          navigate(salesAdminRouteAbsolute.task);
          break;
        case RoleEnum.SALES_PERSON:
          navigate(salesPersonRouteAbsolute.task);
          break;
        case RoleEnum.OPERATIONS_MANAGER:
          navigate(operationsManagerRouteAbsolute.task);
          break;
        case RoleEnum.WAREHOUSE_LEAD:
          navigate(warehouseLeadRouteAbsolute.task);
          break;
        case RoleEnum.WAREHOUSE_STAFF:
          navigate(warehouseStaffRouteAbsolute.task);
          break;
        case RoleEnum.FOREMAN:
          navigate(foremanRouteAbsolute.task);
          break;
        case RoleEnum.CREW:
          navigate(crewRouteAbsolute.task);
          break;
        case RoleEnum.LOADER:
          navigate(loaderRouteAbsolute.task);
          break;
      }
    }
  };
  //#endregion Handle Function

  return (
    <Box id='todaysTaskComponent' className={cx('container')}>
      <Typography className={cx('title')}>{t('dashboard_title_todays_task')}</Typography>

      <Stack className={cx('content')}>
        {listTask && listTask.length > 0 ? (
          listTask.map((item) => (
            <Stack
              key={item?.id}
              className={cx('itemTask', item?.title === 'Overbooking' && 'itemOverbooking')}
              onClick={() => handleDetailTask(item)}
            >
              <Stack direction={'column'} gap={1}>
                <Typography className={cx('titleItem')}>{`${item?.title} ${
                  item?.businessType === BusinessTypeEnum.QUOTE || item?.businessType === BusinessTypeEnum.ORDER
                    ? `#${item?.businessId}`
                    : EMPTY_STRING
                }`}</Typography>

                <Stack direction={'row'} gap={2} alignItems={'center'}>
                  <img
                    src={icons.commonIconCalendar}
                    alt={t('altImg', {
                      ns: 'common/base',
                    })}
                    className={cx('iconDate')}
                  />

                  <Typography className={cx('dateItem')}>{formatDate(item?.dueDate, DateFormatEnum.MMM_D)}</Typography>
                </Stack>
              </Stack>

              <img
                src={icons.commonIconSuccess}
                alt={t('altImg', {
                  ns: 'common/base',
                })}
                className={cx('iconSuccess')}
              />
            </Stack>
          ))
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        )}
      </Stack>

      <BasePagination count={pagination?.totalPages} page={pagination?.page} onChange={handlePageChange} />

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default TodaysTask;
